import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import classNames from 'classnames/bind';
import Helmet from 'react-helmet';
import get from 'lodash/get';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import Button from 'yoda-core-components/lib/components/Button/Button';
import makeQuery from 'yoda-core-components/lib/navigation/query/makeQuery';
import { withRouter } from 'react-router';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import QuickScreenLazy from 'yoda-account-components/lib/components/SYFQuickScreen/QuickScreenLazy';
import { isQuickScreenEligible } from 'yoda-account-components/lib/helpers/Utils/Utils';
import { compose } from 'redux';
import { connect } from 'react-redux';
import ChevronLeftNew from '../../assets/svg/chevron2022.svg';
import Layout from '../Layout/Layout';
import { LayoutContext } from './LayoutContext';
import LayoutHelmet from './LayoutHelmet';
import CAMNavWrapper from './CAMNav';
import { selectRewardsRedesignThrottleFF } from '../../selectors/RewardsRedesignSelector';
import ThrottleFeatureRender from '../ThrottleFeatureRender/ThrottleFeatureRender';
import PLCCBanner from '../PLCCBanner/PLCCBanner';
import * as signInSliderActions from '../../actions/SignInSliderActions';
import * as plccBannerActions from '../../actions/PLCCBannerAction';
import { selectPlccBannerThrottleFF } from '../../selectors/plccBannerSelector';

import * as styles from './WithLayout.css';

const cx = classNames.bind(styles);
// Proxy:export: Should be removed after changing every-where
export { LayoutContext, useLayoutContext } from './LayoutContext';

export class LayoutWrapper extends Component {
    static propTypes = {
        navConfig: PropTypes.oneOfType([PropTypes.object]).isRequired,
        preferences: PropTypes.objectOf(PropTypes.object).isRequired,
        messagesTexts: PropTypes.objectOf(PropTypes.object).isRequired,
        featureFlags: PropTypes.objectOf(PropTypes.object),
        plccBannerDetails: PropTypes.objectOf(PropTypes.any).isRequired,
        signInSliderDetails: PropTypes.objectOf(PropTypes.object),
        enablePlccBanner: PropTypes.bool,
        location: PropTypes.shape({
            pathname: PropTypes.string,
            query: PropTypes.oneOfType([PropTypes.object]),
            search: PropTypes.string,
        }).isRequired,
        classes: PropTypes.shape({
            root: PropTypes.string,
            layoutHeaderClass: PropTypes.string,
            panelContentContainer: PropTypes.string,
            layoutArticleClass: PropTypes.string,
        }),
        callBackProps: PropTypes.objectOf(PropTypes.func),
        history: PropTypes.objectOf(PropTypes.func),
        pageTitle: PropTypes.string,
        layoutState: PropTypes.shape({
            title: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.func]),
            headerPlaceholder: PropTypes.func,
            helmet: PropTypes.String,
            onBackButtonClick: PropTypes.func,
            rewardsErrorObj: PropTypes.object,
        }),
        children: PropTypes.node,
        /* eslint react/no-unused-prop-types: 0 */
        analyticsActions: PropTypes.objectOf(PropTypes.func),
        analyticsAction: PropTypes.objectOf(PropTypes.func),
        userState: PropTypes.string,
        renderRecZone: PropTypes.func,
        hideHeader: PropTypes.bool,
        enableRewardsRelaunch: PropTypes.bool,
        isNative: PropTypes.bool,
    };

    static defaultProps = {
        location: {
            pathname: '',
            query: {},
            search: '',
        },
        classes: {
            root: '',
            layoutHeaderClass: '',
            panelContentContainer: '',
            layoutArticleClass: '',
        },
        callBackProps: null,
        history: undefined,
        pageTitle: '',
        layoutState: {
            title: '',
            headerPlaceholder: () => null,
            helmet: '',
            onBackButtonClick: null,
            rewardsErrorObj: {},
        },
        children: null,
        analyticsActions: null,
        userState: '',
        renderRecZone: null,
        enablePlccBanner: false,
        preferences: {},
        messagesTexts: {},
        plccBannerDetails: {},
        signInSliderDetails: {},
        featureFlags: {},
        hideHeader: false,
        enableRewardsRelaunch: false,
        isNative: false,
    };

    static contextTypes = {
        deviceType: PropTypes.oneOfType([PropTypes.object]).isRequired,
    };

    static childContextTypes = {
        setPageTitle: PropTypes.func,
        setLayoutState: PropTypes.func,
        resetLayoutState: PropTypes.func,
    };

    /* istanbul ignore next */
    constructor(props, context) {
        super(props, context);
        this.state = {
            navView: this.getNavView(props, context),
            layoutState: props.layoutState,
            pageTitle: props.pageTitle,
            isQSBannerEligible: false,
        };

        this.history = get(props, 'history', getBrowserHistory());
    }

    getChildContext() {
        return {
            setPageTitle: this.setPageTitle,
            setLayoutState: this.setLayoutState,
            resetLayoutState: this.resetLayoutState,
        };
    }

    componentDidMount() {
        this.setUpNavPanelLocation();
        const { featureFlags, isNative, preferences } = this.props;
        this.setState({
            isQSBannerEligible: isQuickScreenEligible({ isNative, featureFlags, preferences }),
        });
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { featureFlags, isNative, signInSliderDetails, preferences } = nextProps;
        if (signInSliderDetails?.isQuickScreenEligible) {
            this.setState({
                isQSBannerEligible: isQuickScreenEligible({ isNative, featureFlags, preferences }),
            });
        }
    }

    componentWillUnmount() {
        this.browserHistoryListener && this.browserHistoryListener();
    }

    onLocationChange = (location) => {
        const isMobile = get(this.context, 'deviceType.isMobile', false);

        if (!isMobile) return;

        // entertain location.pop or push only
        // this ensures that we are listening to query string only
        // that too only when fullview isn't there
        // not the route change
        const isSameLocation = this.AreLocationsEqual(location);
        if (location.action !== 'REPLACE' && isSameLocation) {
            const isFUllView = this.state.navView === this.navViewType.fullPage;
            if (!isFUllView) return;

            const query = makeQuery(location.search);
            const showNavPanel = query && query.showNavPanel;

            /* istanbul ignore else */
            if (!showNavPanel) {
                this.setState({ navView: this.navViewType.heading });
            }
        } else if (!isSameLocation) {
            this.setState({ navView: this.getNavView(undefined, undefined, location) });
        }
    };

    setLayoutState = (layoutState) => {
        this.setState({ layoutState });
    };

    setPageTitle = (title) => {
        this.setState({ layoutState: { title } });
    };

    getLocationString = ({ pathname = '' }) =>
        `${pathname.endsWith('/') ? pathname.slice(0, -1) : pathname}`;

    /**
     * Get navView
     * @param {object} props - Component props
     * @param {object} context - Component Context
     */
    getNavView = (props = this.props, context = this.context, location = this.props.location) => {
        let navView;
        const isMobile = get(context, 'deviceType.isMobile', false);
        if (!isMobile) return undefined;

        const query = makeQuery(location.search);
        /* istanbul ignore next */
        if (query.showNavPanel) {
            navView = this.navViewType.fullPage;
            if (props.analyticsActions) {
                const analyticsData = {
                    trackEvent: 'digitalDataReady',
                    pageName: 'CAM Left Nav',
                };
                props.analyticsActions.camAnalyticsAction(analyticsData);
            }
        } else if (this.getLocationString(location) === get(props.navConfig, 'indexUrl', '')) {
            navView = this.navViewType.inline;
        } else {
            navView = this.navViewType.heading;
        }
        return navView;
    };

    setUpNavPanelLocation() {
        const isMobile = get(this.context, 'deviceType.isMobile', false);

        if (!isMobile) return;

        // const showNavPanel = !!get(this.props, 'location.query.showNavPanel');
        // if (showNavPanel) {
        //     // reset
        //     this.pushShowPanelQuery(true);
        // }
        // then
        // listen
        this.browserHistoryListener = this.history && this.history.listen(this.onLocationChange);
    }

    resetLayoutState = () => {
        this.setState({ layoutState: this.props.layoutState });
    };

    AreLocationsEqual = (newLocation, location = this.props.location) => {
        if (isEmpty(newLocation) || isEmpty(location)) return false;
        return this.getLocationString(newLocation) === this.getLocationString(location);
    };

    toggleFullNavView = (e) => {
        e.preventDefault();
        const {
            layoutState: { onBackButtonClick },
        } = this.state;
        /* istanbul ignore next */
        if (typeof onBackButtonClick === 'function') {
            onBackButtonClick();
        } else {
            const isMobile = get(this.context, 'deviceType.isMobile', false);

            if (!isMobile) return;

            this.setState({
                navView: this.navViewType.fullPage,
            });
            this.pushShowPanelQuery();
        }
    };

    handleNavBack = () => {
        const {
            layoutState: { redirectBack },
        } = this.state;
        if (redirectBack) {
            redirectBack();
        } else if (this.props.location.pathname.includes('/account')) {
            getBrowserHistory().push('/account/dashboard');
        } else {
            window.location.pathname = '/account/dashboard';
        }
    };

    pushShowPanelQuery(toRemove = false) {
        const { location } = this.props;
        const oldQuery = makeQuery(location.search) || {};

        const query = toRemove
            ? omit(oldQuery, ['showNavPanel'])
            : { ...oldQuery, showNavPanel: true };

        this.history &&
            this.history.push({
                ...location,
                query,
            });
    }

    navViewType = {
        fullPage: 'fullPage',
        heading: 'heading',
        inline: 'inline',
    };

    /* istanbul ignore next */
    renderNav() {
        const {
            navConfig,
            callBackProps,
            history,
            userState,
            messagesTexts,
            plccBannerDetails,
            preferences,
            enablePlccBanner,
            enableRewardsRelaunch,
            analyticsAction,
        } = this.props;
        const { deviceType } = this.context;
        const classes = { navContainer: cx('navContainer') };
        const isMobile = get(deviceType, 'isMobile', false);
        const highlightActiveItem = !isMobile;
        const camNavProps = omitBy(
            { classes, navConfig, history, callBackProps, highlightActiveItem },
            isNil
        );
        return (
            <>
                <CAMNavWrapper {...camNavProps} />
                {isMobile && (
                    <ThrottleFeatureRender
                        featurePath="enablePlccBanner"
                        headerName="X-ENABLE-PLCC-BANNER"
                        cookieName="DP_ENABLE_PLCC_BANNER"
                    >
                        {
                            /* istanbul ignore next */
                            (isEnabled) => (
                                <>
                                    {isEnabled && (
                                        <div id="signinSlider_creditBanner">
                                            <PLCCBanner
                                                enableRewardsRelaunch={enableRewardsRelaunch}
                                                messagesTexts={messagesTexts}
                                                preferences={preferences}
                                                actions={plccBannerActions}
                                                analyticsAction={analyticsAction}
                                                plccBannerDetails={plccBannerDetails}
                                                signInSliderActions={signInSliderActions}
                                                isSliderBanner
                                                enablePlccBanner={enablePlccBanner}
                                                userState={userState}
                                                deviceType={deviceType}
                                                isCAMNavBanner
                                            />
                                        </div>
                                    )}
                                </>
                            )
                        }
                    </ThrottleFeatureRender>
                )}
            </>
        );
    }

    /* istanbul ignore next */
    render() {
        const { classes, renderRecZone, hideHeader } = this.props;
        const {
            layoutState: { title, helmet, headerPlaceholder, redirectBack, rewardsErrorObj },
            pageTitle,
        } = this.state;
        const isFUllView = this.state.navView === this.navViewType.fullPage;
        const isHeadingView = this.state.navView === this.navViewType.heading;

        const layoutProps = {
            disableWhiteBG: true,
            ...(renderRecZone && { renderRecZone }),
            layoutArticleClass: classes.layoutArticleClass,
            rewardsErrorObj,
        };
        // eslint-disable-next-line no-return-assign
        return (
            /* Context Provider */
            <LayoutContext.Provider
                value={{
                    setLayoutState: this.setLayoutState,
                    resetLayoutState: this.resetLayoutState,
                }}
            >
                <Layout {...layoutProps}>
                    <Helmet title={`${pageTitle || helmet || title} - JCPenney`} />
                    <div
                        className={`${dt(['flex', 'flex-col', 'w-full', 'relative', 'px-6'])} ${cx(
                            'nav-layout-container',
                            'gridView',
                            'tailwindGrid',
                            classes.root
                        )}`}
                    >
                        <div className={dt(['flex', 'items-start', 'flex-wrap'])}>
                            {!hideHeader && (
                                <div
                                    className={dt([
                                        'md:order-1',
                                        'smOnly:order-2',
                                        'w-full',
                                        'text-center',
                                    ])}
                                >
                                    <div
                                        className={`${cx(
                                            'heading-container',
                                            'sm12',
                                            'md8',
                                            'lg8',
                                            'xl8',
                                            this.props.location.pathname === '/account/dashboard' &&
                                                'xl12 lg12 md12',
                                            classes.layoutHeaderClass
                                        )} ${dt([
                                            'relative',
                                            'my-6',
                                            'mx-auto',
                                            '!px-0',
                                            this.props.location.pathname === '/account/dashboard' &&
                                                '!my-4',
                                        ])} `}
                                    >
                                        {this.props.location.pathname !== '/account/dashboard' && (
                                            <Button
                                                buttonType="Text"
                                                className={`${cx('back-icon')} ${dt([
                                                    'absolute',
                                                    'left-0',
                                                    'top-[1px]',
                                                    'rotate-180',
                                                ])}`}
                                                onClick={this.handleNavBack}
                                            >
                                                <Icon
                                                    iconType="svg"
                                                    width="32px"
                                                    height="32px"
                                                    viewBox="0 0 32 32"
                                                >
                                                    <ChevronLeftNew />
                                                </Icon>
                                            </Button>
                                        )}
                                        {typeof title === 'function' ? (
                                            <>{title()}</>
                                        ) : (
                                            <h1
                                                className={`${dt([
                                                    'font-open-sans',
                                                    'text-xlarge',
                                                    'font-bold',
                                                    'text-black',
                                                    'text-center',
                                                    this.props.location.pathname ===
                                                        '/account/dashboard' && '!text-left',
                                                    this.props.location.pathname ===
                                                        '/account/dashboard' &&
                                                        'smOnly:!text-center',
                                                ])} ${cx('page-title')}`}
                                                data-automation-id="Hi_firstname"
                                                data-qm-encrypt="true"
                                            >
                                                {pageTitle || title}
                                            </h1>
                                        )}
                                        {typeof headerPlaceholder === 'function' && (
                                            <>{headerPlaceholder()}</>
                                        )}
                                    </div>
                                </div>
                            )}
                            {!isHeadingView &&
                                this.props.location.pathname === '/account/dashboard' && (
                                    <div
                                        className={dt([
                                            'md:order-2',
                                            'smOnly:order-3',
                                            'md:w-[336px]',
                                            'smOnly:w-full',
                                        ])}
                                    >
                                        {this.renderNav()}
                                    </div>
                                )}
                            {this.state.isQSBannerEligible &&
                                this.props.location.pathname === '/account/dashboard' && (
                                    <div
                                        className={dt([
                                            'md:order-3',
                                            'smOnly:order-1',
                                            'md:w-[374px]',
                                            'smOnly:w-full',
                                        ])}
                                    >
                                        <div
                                            className={dt([
                                                'lg:ml-16',
                                                'lg:mt-32',
                                                'mdOnly:ml-12',
                                                'mdOnly:-mt-6',
                                                'smOnly:mt-6',
                                                'smOnly:mb-2',
                                            ])}
                                        >
                                            <QuickScreenLazy bannerType="Page" />
                                        </div>
                                    </div>
                                )}
                        </div>

                        {isFUllView && <LayoutHelmet layoutState={this.props.layoutState} />}
                        {!isFUllView && (
                            <div
                                className={`${cx(
                                    'panel-content',
                                    'sm12',
                                    'md8',
                                    'lg8',
                                    'xl8',
                                    classes.panelContentContainer
                                )} ${dt(['!p-0', 'mx-auto', 'my-0'])}`}
                            >
                                {this.props.children}
                            </div>
                        )}
                    </div>
                </Layout>
            </LayoutContext.Provider>
        );
    }
}
/* istanbul ignore next */
const mapStateToProps = (state, { navConfig }) => ({
    navConfig:
        navConfig ||
        (selectRewardsRedesignThrottleFF(state)
            ? state.context.preferences.camNav
            : state.context.preferences.camNavLegacy),
    messagesTexts: state.context.messagesTexts,
    preferences: state.context.preferences,
    featureFlags: state.context.featureFlags,
    plccBannerDetails: state.plccBannerDetails,
    signInSliderDetails: state.signInSliderDetails,
    enablePlccBanner: selectPlccBannerThrottleFF(state),
    userState: get(state.accounts, 'userProfile.userState', ''),
    isNative: get(state, 'context.isNative', false),
    enableRewardsRelaunch: get(state.context, 'featureFlags.enableRewardsRelaunch', false),
});

export default compose(withRouter, connect(mapStateToProps))(LayoutWrapper);
